// ============================================================================
//  ManageConsole.jsx — /manage. Roster and team administration.
//
//  Progressively revealed by rank:
//    Team Admin  — his team: rename, invite, suspend
//    Admin       — every team he leads, plus create team, assign leaders,
//                  move men between his teams, promote up to Team Admin
//    Super Admin — all of that everywhere, plus the Unassigned bucket
//
//  Every action maps to one RPC and shows that RPC's message verbatim on
//  failure. The server messages are written to be read by a man, which is why
//  nothing here rewrites them.
// ============================================================================

import React, { useState, useEffect, useCallback } from "react";
import {
  UserPlus, Users, Plus, Edit3, Check, X, Shield, ArrowRightLeft, AlertTriangle,
  Mail, KeyRound,
} from "lucide-react";
import { C, display, kicker, lbl, inp, primaryBtn, smallBtn, smallGhost } from "../theme/tokens.js";
import { Avatar, Pill, Modal } from "../theme/primitives.jsx";
import {
  canManage, canCreateTeams, canEditTeam, grantableRoles, label as roleLabel,
  ROLE_BLURB, capacityOf, leadsTeam, effectiveRank,
} from "../lib/roles.js";
import {
  createTeam, renameTeam, setTeamCapacity, assignLeader, unassignLeader,
  moveManToTeam, setManRole, setProfileSuspended,
  loadAccessStatus, resendForMan, setTempPassword,
} from "../lib/storage.js";

export default function ManageConsole({ st, me, selfId, onChanged, onInvite, onBulkInvite }) {
  const led = st.myLedGroupIds ?? [];
  const isSuper = me?.role === "super_admin";

  const teamIds = Object.keys(st.groups ?? {})
    .filter((id) => isSuper || led.includes(id))
    .sort((a, b) => (st.groups[a]?.name ?? "").localeCompare(st.groups[b]?.name ?? ""));

  const unassigned = Object.values(st.people).filter((p) => !p.group_id);

  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [tempFor, setTempFor] = useState(null);

  // Who has actually accepted his invite. profiles rows exist from the moment
  // an invite is sent, so without this the roster shows a man invited six
  // weeks ago exactly the same as one who signed in this morning — which is
  // why nobody could see who was stuck. Best effort: a leader who cannot reach
  // the function still gets the rest of the console.
  const [access, setAccess] = useState(null);
  const refreshAccess = useCallback(() => {
    let live = true;
    loadAccessStatus()
      .then((a) => { if (live) setAccess(a); })
      .catch(() => { if (live) setAccess({}); });
    return () => { live = false; };
  }, []);
  useEffect(refreshAccess, [refreshAccess]);

  // Every mutation funnels through here so one place owns the error surface
  // and the reload. onChanged refetches; optimistic patching across four
  // interlocking tables is not worth the bugs.
  async function run(key, fn) {
    setBusy(key);
    setErr("");
    setNote("");
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setErr(e?.message || "That did not work.");
    } finally {
      setBusy("");
    }
  }

  // Account recovery does not change any of the data onChanged refetches, and
  // it has something to say on success, so it gets its own runner rather than
  // a silent reload.
  async function recover(key, fn) {
    setBusy(key);
    setErr("");
    setNote("");
    try {
      const res = await fn();
      setNote(res?.message || "Done.");
      refreshAccess();
      return res;
    } catch (e) {
      setErr(e?.message || "That did not work.");
      throw e;
    } finally {
      setBusy("");
    }
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px 80px" }}>
      <div style={{ padding: "36px 0 10px" }}>
        <div style={kicker}>Administration</div>
        <h1 style={{ ...display, fontSize: 36, margin: "8px 0 6px", color: C.text }}>Manage</h1>
        <p style={{ fontSize: 14, color: C.sub, maxWidth: 600, lineHeight: 1.6 }}>
          {isSuper
            ? "Every team and every man."
            : teamIds.length > 1
              ? "The teams you lead."
              : "Your team."}
        </p>
      </div>

      {err && (
        <div style={{ display: "flex", gap: 9, alignItems: "flex-start", background: "rgba(213,0,50,0.06)",
          border: `1px solid ${C.redLine}`, borderRadius: 10, padding: "12px 14px", marginBottom: 18 }}>
          <AlertTriangle size={15} color={C.redDk} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: C.redDk, lineHeight: 1.5 }}>{err}</div>
        </div>
      )}

      {note && (
        <div style={{ display: "flex", gap: 9, alignItems: "flex-start", background: "rgba(46,139,87,0.07)",
          border: "1px solid rgba(46,139,87,0.28)", borderRadius: 10, padding: "12px 14px", marginBottom: 18 }}>
          <Check size={15} color="#1F6B40" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: "#1F6B40", lineHeight: 1.5, flex: 1 }}>{note}</div>
          <button onClick={() => setNote("")} style={{ ...smallGhost, padding: 2 }} aria-label="Dismiss">
            <X size={13} />
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 26 }}>
        <button onClick={onInvite} style={primaryBtn}>
          <UserPlus size={15} /> Invite a man
        </button>
        <button onClick={onBulkInvite} style={smallBtn}>
          <Users size={14} /> Invite a whole cohort
        </button>
        {canCreateTeams(me) && (
          <button onClick={() => setCreating(true)} style={smallGhost}>
            <Plus size={14} /> New team
          </button>
        )}
      </div>

      {teamIds.length === 0 && (
        <div style={{ background: C.panel, border: `1px dashed ${C.line}`, borderRadius: 14,
          padding: "34px 24px", textAlign: "center", color: C.sub, fontSize: 13.5 }}>
          You do not lead a team yet.
          {canCreateTeams(me) ? " Create one to get started." : " Ask an Admin to assign you one."}
        </div>
      )}

      {teamIds.map((id) => (
        <TeamPanel key={id} groupId={id} st={st} me={me} selfId={selfId} led={led}
          busy={busy} run={run} recover={recover} access={access}
          onTempPassword={setTempFor} onInvite={onInvite} />
      ))}

      {isSuper && unassigned.length > 0 && (
        <div style={{ marginTop: 30, background: C.panel, border: `1px solid ${C.line}`,
          borderRadius: 14, padding: "20px 22px" }}>
          <div style={{ ...kicker, marginBottom: 4 }}>Needs a team</div>
          <h3 style={{ ...display, fontSize: 19, margin: "0 0 4px", color: C.text }}>
            Unassigned ({unassigned.length})
          </h3>
          <p style={{ fontSize: 12.5, color: C.sub, margin: "0 0 14px", lineHeight: 1.55 }}>
            These men belong to no cohort, so nobody but you can see them. Move
            them onto a team.
          </p>
          {unassigned.map((p) => (
            <ManRow key={p.id} p={p} st={st} me={me} selfId={selfId} led={led}
              busy={busy} run={run} recover={recover} access={access?.[p.id]}
              onTempPassword={setTempFor} />
          ))}
        </div>
      )}

      {creating && (
        <CreateTeamModal
          onClose={() => setCreating(false)}
          onCreate={async (name, cap) => {
            await run("create", () => createTeam(name, cap));
            setCreating(false);
          }}
          busy={busy === "create"}
        />
      )}

      {tempFor && (
        <TempPasswordModal
          man={tempFor}
          busy={busy === `temp-${tempFor.id}`}
          onClose={() => setTempFor(null)}
          onSet={async (pw) => {
            await recover(`temp-${tempFor.id}`, () => setTempPassword(tempFor.id, pw));
            setTempFor(null);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function TeamPanel({ groupId, st, me, selfId, led, busy, run, recover, access,
  onTempPassword, onInvite }) {
  const team = st.groups[groupId];
  const cap = capacityOf(st.people, st.groups, groupId);
  const men = Object.values(st.people).filter((p) => p.group_id === groupId);
  const leaderIds = st.leaders?.[groupId] ?? [];
  const editable = canEditTeam(me, led, groupId);

  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(team?.name ?? "");
  const [capOpen, setCapOpen] = useState(false);
  const [capValue, setCapValue] = useState(cap.capacity);

  return (
    <div style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 14,
      padding: "20px 22px", marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {renaming ? (
          <>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
              style={{ ...inp, marginTop: 0, maxWidth: 260 }} />
            <button style={smallBtn} disabled={busy === `rename-${groupId}`}
              onClick={async () => {
                await run(`rename-${groupId}`, () => renameTeam(groupId, name));
                setRenaming(false);
              }}>
              <Check size={14} /> Save
            </button>
            <button style={smallGhost} onClick={() => { setName(team?.name ?? ""); setRenaming(false); }}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <h3 style={{ ...display, fontSize: 21, margin: 0, color: C.text }}>{team?.name}</h3>
            <span style={{ fontSize: 12.5, color: cap.full ? C.redDk : C.sub,
              fontWeight: cap.full ? 700 : 400 }}>
              {cap.used} of {cap.capacity}{cap.full ? " · full" : ""}
            </span>
            {editable && (
              <button style={{ ...smallGhost, marginLeft: "auto" }} onClick={() => setRenaming(true)}>
                <Edit3 size={13} /> Rename
              </button>
            )}
            {editable && (
              <button style={smallGhost} onClick={() => setCapOpen((v) => !v)}>
                Capacity
              </button>
            )}
          </>
        )}
      </div>

      {capOpen && editable && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12,
          background: C.shell, borderRadius: 9, padding: "10px 12px" }}>
          <label style={{ ...lbl, marginRight: 4 }}>Max men</label>
          <input type="number" min="1" max="500" value={capValue}
            onChange={(e) => setCapValue(Number(e.target.value))}
            style={{ ...inp, marginTop: 0, width: 90 }} />
          <button style={smallBtn} disabled={busy === `cap-${groupId}`}
            onClick={async () => {
              await run(`cap-${groupId}`, () => setTeamCapacity(groupId, capValue));
              setCapOpen(false);
            }}>
            <Check size={14} /> Set
          </button>
          <button style={smallGhost} onClick={() => setCapOpen(false)}>Cancel</button>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        {men.length === 0 ? (
          <div style={{ fontSize: 13, color: C.faint, padding: "10px 0" }}>
            Nobody on this team yet.{" "}
            <button onClick={onInvite} style={{ ...smallGhost, padding: 0, display: "inline" }}>
              Invite the first man.
            </button>
          </div>
        ) : (
          men.map((p) => (
            <ManRow key={p.id} p={p} st={st} me={me} selfId={selfId} led={led}
              groupId={groupId} isLeader={leaderIds.includes(p.id)}
              leaderCount={leaderIds.length} busy={busy} run={run}
              recover={recover} access={access?.[p.id]} onTempPassword={onTempPassword} />
          ))
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ManRow({ p, st, me, selfId, led, groupId, isLeader, leaderCount, busy, run,
  recover, access, onTempPassword }) {
  const manageable = canManage(me, p, led);
  const grantable = grantableRoles(me);
  const isSelf = p.id === selfId;
  const key = `man-${p.id}`;
  const working = busy === key;

  // access is undefined while the status call is in flight, and for a man the
  // caller cannot manage. Only claim someone is stuck once we actually know.
  const stuck = access ? !access.confirmed : false;
  const neverSignedIn = access ? !access.last_sign_in_at : false;
  const canSetPassword = effectiveRank(me) >= 3;

  // Teams this man could be moved to: ones the viewer leads, minus his own.
  const moveTargets = Object.keys(st.groups ?? {})
    .filter((id) => (me?.role === "super_admin" || led.includes(id)) && id !== p.group_id);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 0",
      borderTop: `1px solid ${C.line}`, flexWrap: "wrap",
      opacity: p.is_suspended ? 0.6 : 1 }}>
      <Avatar person={p} size={34} />
      <div style={{ minWidth: 130, flex: "1 1 130px" }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: C.text }}>
          {p.name}{isSelf && <span style={{ color: C.faint, fontWeight: 400 }}> (you)</span>}
        </div>
        <div style={{ display: "flex", gap: 5, marginTop: 3, flexWrap: "wrap" }}>
          <Pill label={roleLabel(p.role)} tone={p.role === "mentee" ? "neutral" : "ink"} />
          {isLeader && <Pill label="Leads" tone="red" />}
          {p.is_suspended && <Pill label="Suspended" />}
          {/* The state the ministry could not see. A man sitting on "Never
              accepted" for weeks is the whole problem this release is about. */}
          {stuck && <Pill label="Never accepted" tone="red" />}
          {!stuck && neverSignedIn && <Pill label="Never signed in" />}
        </div>
        {access?.email && (
          <div style={{ fontSize: 11.5, color: C.faint, marginTop: 3, wordBreak: "break-all" }}>
            {access.email}
          </div>
        )}
      </div>

      {!manageable ? (
        <div style={{ fontSize: 11.5, color: C.faint, marginLeft: "auto" }}>
          {isSelf ? "You cannot manage yourself" : "Above your level"}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap",
          alignItems: "center" }}>
          {grantable.length > 0 && (
            <select value={p.role} disabled={working}
              onChange={(e) => run(key, () => setManRole(p.id, e.target.value))}
              title="Change role"
              style={selectStyle}>
              {[...new Set([p.role, ...grantable])].map((r) => (
                <option key={r} value={r} disabled={!grantable.includes(r)}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
          )}

          {groupId && (
            isLeader ? (
              <button style={smallGhost} disabled={working}
                title={leaderCount <= 1 ? "This team would be left with no leader" : "Remove as team leader"}
                onClick={() => run(key, () => unassignLeader(p.id, groupId))}>
                <Shield size={13} /> Unlead
              </button>
            ) : (
              <button style={smallGhost} disabled={working}
                title="Make a leader of this team"
                onClick={() => run(key, () => assignLeader(p.id, groupId))}>
                <Shield size={13} /> Make leader
              </button>
            )
          )}

          {moveTargets.length > 0 && (
            <select value="" disabled={working}
              onChange={(e) => { if (e.target.value) run(key, () => moveManToTeam(p.id, e.target.value)); }}
              title="Move to another team"
              style={selectStyle}>
              <option value="">Move to…</option>
              {moveTargets.map((id) => {
                const c = capacityOf(st.people, st.groups, id);
                return (
                  <option key={id} value={id} disabled={c.full}>
                    {st.groups[id].name}{c.full ? " (full)" : ` (${c.used}/${c.capacity})`}
                  </option>
                );
              })}
            </select>
          )}

          {/* Account recovery. Shown for every manageable man, not only the
              stuck ones — "he says he never got it" arrives before any status
              flag does, and hunting for a button that appears conditionally is
              its own small failure. */}
          <button style={stuck ? smallBtn : smallGhost} disabled={busy === `resend-${p.id}`}
            title={stuck
              ? "He has never accepted his invite. Send a fresh link."
              : "Email him a password reset link."}
            onClick={() => recover(`resend-${p.id}`, () => resendForMan(p.id)).catch(() => {})}>
            <Mail size={13} /> {busy === `resend-${p.id}`
              ? "Sending…"
              : stuck ? "Resend invite" : "Send reset"}
          </button>

          {canSetPassword && (
            <button style={smallGhost} disabled={working}
              title="Set a temporary password to hand him in person"
              onClick={() => onTempPassword({ id: p.id, name: p.name, stuck })}>
              <KeyRound size={13} /> Password
            </button>
          )}

          <button style={smallGhost} disabled={working}
            onClick={() => {
              const verb = p.is_suspended ? "unsuspend" : "suspend";
              if (window.confirm(`Are you sure you want to ${verb} ${p.name}?`)) {
                run(key, () => setProfileSuspended(p.id, !p.is_suspended));
              }
            }}>
            {p.is_suspended ? "Restore" : "Suspend"}
          </button>
        </div>
      )}
    </div>
  );
}

const selectStyle = {
  padding: "6px 9px", borderRadius: 7, border: `1px solid ${C.line}`,
  background: "#FBFBFB", fontSize: 12.5, color: C.text,
  fontFamily: "'Barlow', sans-serif", cursor: "pointer",
};

/* ------------------------------------------------------------------ */

function CreateTeamModal({ onClose, onCreate, busy }) {
  const [name, setName] = useState("");
  const [cap, setCap] = useState(15);
  return (
    <Modal title="New team" onClose={onClose} maxWidth={440}>
      <label style={lbl}>Team name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
        placeholder="Tuesday 6AM" style={inp} />

      <label style={{ ...lbl, display: "block", marginTop: 14 }}>Maximum men</label>
      <input type="number" min="1" max="500" value={cap}
        onChange={(e) => setCap(Number(e.target.value))} style={inp} />
      <div style={{ fontSize: 12, color: C.faint, marginTop: 6, lineHeight: 1.5 }}>
        Counts leaders too. You can change this later.
      </div>

      <button onClick={() => name.trim() && onCreate(name.trim(), cap)}
        disabled={busy || !name.trim()}
        style={{ ...primaryBtn, width: "100%", marginTop: 20, justifyContent: "center",
          opacity: busy || !name.trim() ? 0.6 : 1 }}>
        <Plus size={15} /> {busy ? "Creating…" : "Create team"}
      </button>
      <div style={{ fontSize: 11.5, color: C.faint, textAlign: "center", marginTop: 10 }}>
        You will lead this team.
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Break glass. For the man whose email is simply not working — wrong address
 * on the roster, a bouncing work account, a mailbox he cannot open on his
 * phone. Rather than leave him outside the app for another month, an Admin
 * sets a password and hands it to him across a table at the meeting.
 *
 * The copy here does not soften what this is. It is the one action in Mile
 * Marker that lets one man sign in as another, and it is written down in
 * access_events every time.
 */
function TempPasswordModal({ man, busy, onClose, onSet }) {
  // Three short words are easier to read aloud and retype on a phone than a
  // random string, and still well past the 10-character floor.
  const suggest = () => {
    const words = ["anchor", "granite", "compass", "harbor", "timber", "summit",
      "forge", "beacon", "ridge", "anvil", "quarry", "lantern"];
    const pick = () => words[Math.floor(Math.random() * words.length)];
    return `${pick()}-${pick()}-${Math.floor(Math.random() * 90 + 10)}`;
  };
  const [pw, setPw] = useState(suggest);

  return (
    <Modal title="Set a temporary password" onClose={onClose} maxWidth={460}>
      <p style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.6, margin: "0 0 16px" }}>
        For <strong style={{ color: C.text }}>{man.name}</strong>
        {man.stuck ? ", who has never accepted his invite." : "."} Use this when
        his email is the thing that is broken. He will be able to sign in
        immediately with his email address and this password.
      </p>

      <label style={lbl}>Password</label>
      <div style={{ display: "flex", gap: 8 }}>
        <input value={pw} onChange={(e) => setPw(e.target.value)}
          style={{ ...inp, fontFamily: "ui-monospace, monospace", letterSpacing: ".02em" }} />
        <button type="button" style={smallGhost} onClick={() => setPw(suggest())}
          title="Suggest another">
          New
        </button>
      </div>
      <div style={{ fontSize: 12, color: C.faint, marginTop: 6, lineHeight: 1.5 }}>
        At least 10 characters. Read it to him in person or over the phone.
      </div>

      <div style={{ marginTop: 16, background: "rgba(213,0,50,0.05)", border: `1px solid ${C.redLine}`,
        borderRadius: 10, padding: "12px 14px", display: "flex", gap: 9, alignItems: "flex-start" }}>
        <AlertTriangle size={15} color={C.redDk} style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 12.5, color: C.redDk, lineHeight: 1.55 }}>
          Anyone holding this password can sign in as {man.name} and read
          everything he has written, including goals he marked private. Do not
          send it by text or email, and tell him to change it under his profile
          once he is in. This action is recorded.
        </div>
      </div>

      <button onClick={() => pw.trim().length >= 10 && onSet(pw.trim())}
        disabled={busy || pw.trim().length < 10}
        style={{ ...primaryBtn, width: "100%", marginTop: 20, justifyContent: "center",
          opacity: busy || pw.trim().length < 10 ? 0.6 : 1 }}>
        <KeyRound size={15} /> {busy ? "Setting…" : "Set this password"}
      </button>
    </Modal>
  );
}
